import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { 
  Home, ArrowUpCircle, ArrowLeftRight, Send, CreditCard, Users, History, 
  Shield, Settings, QrCode, Link2, Code, Grid3X3, Award, HelpCircle, 
  GraduationCap, LogOut, ChevronLeft, Check, Phone, Mail, Bell, Copy
} from "lucide-react";

// Generate random values outside component
const generateRandomId = () => Math.floor(100000 + Math.random() * 900000);
const generateBrowserTheme = () => 'dark'; // Default dark theme

export default function CashinoutPreview() {
  const [searchParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [menuCollapsed, setMenuCollapsed] = useState(false);
  const [browserTheme, setBrowserTheme] = useState(generateBrowserTheme);
  const [showBrowser, setShowBrowser] = useState(true);
  const [randomUserId] = useState(generateRandomId);

  // Set browser title and favicon
  useEffect(() => {
    document.title = 'Настройки пользователя';
    
    // Check for theme in URL params
    const urlParams = new URLSearchParams(window.location.search);
    const themeParam = urlParams.get('theme');
    if (themeParam === 'dark' || themeParam === 'light') {
      setBrowserTheme(themeParam);
    }
    
    // Set favicon
    let favicon = document.querySelector("link[rel='icon']");
    if (!favicon) {
      favicon = document.createElement('link');
      favicon.rel = 'icon';
      document.head.appendChild(favicon);
    }
    favicon.href = '/cashinout-favicon.ico';
    
    return () => {
      // Reset title on unmount
      document.title = 'CRM Platform';
    };
  }, []);

  useEffect(() => {
    const dataParam = searchParams.get('data');
    if (dataParam) {
      try {
        const parsedData = JSON.parse(decodeURIComponent(dataParam));
        setData(parsedData);
        
        // Read showBrowser from data
        if (parsedData.showBrowser !== undefined) {
          setShowBrowser(parsedData.showBrowser);
        }
        
        // Read browserTheme from data
        if (parsedData.browserTheme) {
          setBrowserTheme(parsedData.browserTheme);
        }
      } catch (e) {
        console.error("Error parsing data:", e);
      }
    }
  }, [searchParams]);

  if (!data) {
    return (
      <div className="min-h-screen bg-[#f5f7fa] flex items-center justify-center">
        <div className="text-gray-600">Загрузка...</div>
      </div>
    );
  }

  const accountName = `${data.firstName} ${data.lastName}`;
  const phoneNumber = data.phone || "+7 (999) 123-45-67";
  const email = data.email || "user@example.com";
  const userId = data.cashinoutId || randomUserId;

  const menuItems = [
    { icon: Home, label: 'Главная', active: false },
    { icon: ArrowUpCircle, label: 'Пополнить', active: false },
    { icon: ArrowLeftRight, label: 'Обмен', active: false },
    { icon: Send, label: 'Отправить', active: false },
    { icon: CreditCard, label: 'Виртуальные карты', active: false },
    { icon: Users, label: 'Реферальная программа', active: false },
    { icon: History, label: 'История', active: false },
    { icon: Shield, label: '2FA', active: false },
    { icon: Settings, label: 'Настройки', active: true },
  ];

  const appItems = [
    { icon: QrCode, label: 'QR авторизация' },
  ];

  const businessItems = [
    { icon: Link2, label: 'Платёжные ссылки' },
    { icon: Code, label: 'API' },
    { icon: Grid3X3, label: 'Массовые выплаты' },
  ];

  const bottomItems = [
    { icon: Award, label: 'Тарифы' },
    { icon: HelpCircle, label: 'Поддержка' },
    { icon: GraduationCap, label: 'Академия Cashinout' },
    { icon: HelpCircle, label: 'FAQ' },
    { icon: LogOut, label: 'Выйти' },
  ];

  // Theme colors
  const isDark = browserTheme === 'dark';
  const theme = {
    bg: isDark ? '#202124' : '#dee1e6',
    tabBarBg: isDark ? '#202124' : '#dee1e6',
    activeTabBg: isDark ? '#292a2d' : '#ffffff',
    inactiveTabBg: isDark ? '#35363a' : '#c9cdd4',
    addressBarBg: isDark ? '#292a2d' : '#ffffff',
    addressInputBg: isDark ? '#202124' : '#f1f3f4',
    textPrimary: isDark ? '#e8eaed' : '#202124',
    textSecondary: isDark ? '#9aa0a6' : '#5f6368',
    hoverBg: isDark ? '#3c3d41' : '#e8eaed',
    border: isDark ? '#3c4043' : '#dadce0',
  };
  // Set body background to match theme to avoid dark strips in screenshots  useEffect(() => {    document.documentElement.style.backgroundColor = theme.bg;    document.body.style.backgroundColor = theme.bg;    document.body.style.margin = "0";    document.body.style.padding = "0";    return () => {      document.documentElement.style.backgroundColor = "";      document.body.style.backgroundColor = "";    };  }, [theme.bg]);

  // Browser frame component
  const BrowserFrame = () => (
    <div style={{ backgroundColor: theme.tabBarBg }} className="flex-shrink-0">
      <div className="flex items-center h-[38px]" style={{ backgroundColor: theme.tabBarBg }}>
        <div className="flex items-end flex-1 pl-2">
          {/* Tab 1 - Ozon Карта */}
          <div className="flex items-center h-[34px] px-3 rounded-t-lg min-w-[140px] max-w-[200px] mr-[1px] group cursor-pointer" style={{ backgroundColor: theme.inactiveTabBg }}>
            <img src="/ozon-favicon.ico" alt="" className="w-4 h-4 mr-2 flex-shrink-0" />
            <span className="text-[13px] truncate flex-1" style={{ color: theme.textSecondary }}>Ozon Карта</span>
          </div>
          {/* Tab 2 - Настройки (ACTIVE) */}
          <div className="flex items-center h-[34px] px-3 rounded-t-lg min-w-[140px] max-w-[200px] mr-[1px] group cursor-pointer" style={{ backgroundColor: theme.activeTabBg }}>
            <img src="/cashinout-favicon.ico" alt="" className="w-4 h-4 mr-2 flex-shrink-0" />
            <span className="text-[13px] truncate flex-1" style={{ color: theme.textPrimary }}>Настройки пользователя</span>
          </div>
          <button className="w-7 h-7 flex items-center justify-center rounded-full ml-1 mb-1" style={{ color: theme.textSecondary }}>
            <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor"><path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z"/></svg>
          </button>
        </div>
        <div className="flex items-center h-full">
          <button className="w-[46px] h-full flex items-center justify-center hover:bg-[#3c3d41]" style={{ color: theme.textSecondary }}><svg className="w-[10px] h-[1px]" viewBox="0 0 10 1"><rect fill="currentColor" width="10" height="1"/></svg></button>
          <button className="w-[46px] h-full flex items-center justify-center hover:bg-[#3c3d41]" style={{ color: theme.textSecondary }}><svg className="w-[10px] h-[10px]" viewBox="0 0 10 10" fill="none" stroke="currentColor" strokeWidth="1"><rect x="0.5" y="0.5" width="9" height="9"/></svg></button>
          <button className="w-[46px] h-full flex items-center justify-center hover:bg-[#e81123] hover:text-white" style={{ color: theme.textSecondary }}><svg className="w-[10px] h-[10px]" viewBox="0 0 10 10" stroke="currentColor" strokeWidth="1.2"><line x1="0" y1="0" x2="10" y2="10"/><line x1="10" y1="0" x2="0" y2="10"/></svg></button>
        </div>
      </div>
      <div className="flex items-center h-[40px] px-2" style={{ backgroundColor: theme.addressBarBg }}>
        <div className="flex items-center gap-1 mr-2">
          <button className="w-8 h-8 flex items-center justify-center rounded-full" style={{ color: theme.textSecondary }}><svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor"><path d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z"/></svg></button>
          <button className="w-8 h-8 flex items-center justify-center rounded-full" style={{ color: isDark ? '#5f6368' : '#dadce0' }}><svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor"><path d="M12 4l-1.41 1.41L16.17 11H4v2h12.17l-5.58 5.59L12 20l8-8z"/></svg></button>
          <button className="w-8 h-8 flex items-center justify-center rounded-full" style={{ color: theme.textSecondary }}><svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor"><path d="M17.65 6.35C16.2 4.9 14.21 4 12 4c-4.42 0-7.99 3.58-7.99 8s3.57 8 7.99 8c3.73 0 6.84-2.55 7.73-6h-2.08c-.82 2.33-3.04 4-5.65 4-3.31 0-6-2.69-6-6s2.69-6 6-6c1.66 0 3.14.69 4.22 1.78L13 11h7V4l-2.35 2.35z"/></svg></button>
        </div>
        <div className="flex-1 flex items-center h-[32px] px-4 rounded-full" style={{ backgroundColor: theme.addressInputBg, border: `1px solid ${theme.border}` }}>
          <svg className="w-4 h-4 mr-3 flex-shrink-0" style={{ color: theme.textSecondary }} viewBox="0 0 24 24" fill="currentColor"><path d="M18 8h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2zm-6 9c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2zm3.1-9H8.9V6c0-1.71 1.39-3.1 3.1-3.1 1.71 0 3.1 1.39 3.1 3.1v2z"/></svg>
          <span className="text-[14px]" style={{ color: theme.textPrimary }}>cashinout.io/profile</span>
        </div>
        <div className="flex items-center gap-1 ml-3">
          <button className="w-8 h-8 flex items-center justify-center rounded-full" style={{ color: theme.textSecondary }}><svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor"><path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z"/></svg></button>
          <button className="w-8 h-8 flex items-center justify-center rounded-full" style={{ color: theme.textSecondary }}><svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor"><circle cx="12" cy="12" r="2"/><circle cx="12" cy="5" r="2"/><circle cx="12" cy="19" r="2"/></svg></button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen flex flex-col template-preview-scale" style={{ backgroundColor: showBrowser ? theme.bg : '#f5f7fa', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif' }}>
      {showBrowser && <BrowserFrame />}

      {/* Main Content (Website) */}
      <div className="flex-1 flex bg-[#f5f7fa]">
        {/* Sidebar */}
        <aside className={`${menuCollapsed ? 'w-16' : 'w-56'} bg-[#303646] flex flex-col transition-all duration-300 flex-shrink-0`}>
        {/* Logo */}
        <div className="p-4 flex items-center gap-3">
          <img 
            src="/cashinout-logo.png" 
            alt="Cashinout" 
            className={`${menuCollapsed ? 'w-8' : 'h-8'} object-contain`}
          />
        </div>

        {/* Main Menu */}
        <nav className="flex-1 py-2 overflow-y-auto">
          {menuItems.map((item, index) => {
            const Icon = item.icon;
            return (
              <a
                key={index}
                href="#"
                className={`flex items-center gap-3 px-4 py-2.5 text-sm transition-colors ${
                  item.active 
                    ? 'text-[#00c853] bg-[#3d4557]' 
                    : 'text-[#a0aec0] hover:bg-[#3d4557] hover:text-white'
                }`}
              >
                <Icon className="w-5 h-5 flex-shrink-0" />
                {!menuCollapsed && <span>{item.label}</span>}
              </a>
            );
          })}

          {/* App Section */}
          {!menuCollapsed && (
            <div className="px-4 py-2 mt-2">
              <span className="text-xs font-medium text-[#6b7280] uppercase">Приложение</span>
            </div>
          )}
          {appItems.map((item, index) => {
            const Icon = item.icon;
            return (
              <a
                key={index}
                href="#"
                className="flex items-center gap-3 px-4 py-2.5 text-sm text-[#a0aec0] hover:bg-[#3d4557] hover:text-white transition-colors"
              >
                <Icon className="w-5 h-5 flex-shrink-0" />
                {!menuCollapsed && <span>{item.label}</span>}
              </a>
            );
          })}

          {/* For Business Section */}
          {!menuCollapsed && (
            <div className="px-4 py-2 mt-2">
              <span className="text-xs font-medium text-[#6b7280] uppercase">Для бизнеса</span>
            </div>
          )}
          {businessItems.map((item, index) => {
            const Icon = item.icon;
            return (
              <a
                key={index}
                href="#"
                className="flex items-center gap-3 px-4 py-2.5 text-sm text-[#a0aec0] hover:bg-[#3d4557] hover:text-white transition-colors"
              >
                <Icon className="w-5 h-5 flex-shrink-0" />
                {!menuCollapsed && <span>{item.label}</span>}
              </a>
            );
          })}

          {/* Bottom Items */}
          <div className="mt-4 border-t border-[#3d4557] pt-2">
            {bottomItems.map((item, index) => {
              const Icon = item.icon;
              return (
                <a
                  key={index}
                  href="#"
                  className="flex items-center gap-3 px-4 py-2.5 text-sm text-[#a0aec0] hover:bg-[#3d4557] hover:text-white transition-colors"
                >
                  <Icon className="w-5 h-5 flex-shrink-0" />
                  {!menuCollapsed && <span>{item.label}</span>}
                </a>
              );
            })}
          </div>
        </nav>

        {/* Hide Menu Toggle */}
        <button 
          onClick={() => setMenuCollapsed(!menuCollapsed)}
          className="flex items-center gap-2 px-4 py-3 text-sm text-[#a0aec0] hover:bg-[#3d4557] border-t border-[#3d4557]"
        >
          <ChevronLeft className={`w-5 h-5 transition-transform ${menuCollapsed ? 'rotate-180' : ''}`} />
          {!menuCollapsed && <span>Скрыть меню</span>}
        </button>
      </aside>

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Header */}
        <header className="bg-white border-b border-gray-200 px-6 py-3 flex items-center justify-end gap-4">
          {/* Language */}
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <span className="text-lg">🇷🇺</span>
            <ChevronLeft className="w-4 h-4 rotate-[-90deg]" />
          </div>
          
          {/* User Name */}
          <span className="text-sm text-gray-700 font-medium">{accountName}</span>
          
          {/* User Avatar */}
          <div className="w-8 h-8 bg-gradient-to-br from-blue-400 to-blue-600 rounded-full flex items-center justify-center">
            <span className="text-white text-xs font-medium">
              {accountName.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)}
            </span>
          </div>
          
          {/* ID */}
          <div className="flex items-center gap-1 text-sm text-gray-600">
            <span>ID:</span>
            <span className="font-medium">{userId}</span>
          </div>
          
          {/* Icons */}
          <button className="p-2 text-gray-500 hover:text-gray-700">
            <Copy className="w-5 h-5" />
          </button>
          <button className="p-2 text-gray-500 hover:text-gray-700 relative">
            <Bell className="w-5 h-5" />
            <span className="absolute top-1 right-1 w-2 h-2 bg-blue-500 rounded-full"></span>
          </button>
        </header>

        {/* Page Content */}
        <main className="flex-1 p-8 overflow-auto">
          <div className="max-w-2xl">
            <h1 className="text-2xl font-semibold text-[#1a1a2e] mb-8">Настройки</h1>

            {/* Account Name */}
            <div className="mb-6">
              <label className="block text-sm text-[#64748b] mb-2">Имя аккаунта:</label>
              <div className="relative">
                <input
                  type="text"
                  value={accountName}
                  readOnly
                  className="w-full px-4 py-3 border border-gray-200 rounded-lg text-[#1a1a2e] bg-white focus:outline-none focus:border-[#00c853]"
                />
                <button className="absolute right-2 top-1/2 transform -translate-y-1/2 w-8 h-8 bg-[#00c853] rounded flex items-center justify-center">
                  <Check className="w-4 h-4 text-white" />
                </button>
              </div>
            </div>

            {/* Phone Number */}
            <div className="mb-6">
              <label className="block text-sm text-[#64748b] mb-2">Номер телефона:</label>
              <div className="relative">
                <div className="absolute left-3 top-1/2 transform -translate-y-1/2">
                  <Phone className="w-5 h-5 text-[#64748b]" />
                </div>
                <input
                  type="text"
                  value={phoneNumber}
                  readOnly
                  className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-lg text-[#1a1a2e] bg-white focus:outline-none focus:border-[#00c853]"
                />
                <div className="absolute right-3 top-1/2 transform -translate-y-1/2 flex items-center gap-1 text-[#00c853] text-sm">
                  <Check className="w-4 h-4" />
                  <span>Подтверждён</span>
                </div>
              </div>
            </div>

            {/* Email */}
            <div className="mb-6">
              <label className="block text-sm text-[#64748b] mb-2">Email:</label>
              <div className="relative">
                <div className="absolute left-3 top-1/2 transform -translate-y-1/2">
                  <Mail className="w-5 h-5 text-[#64748b]" />
                </div>
                <input
                  type="text"
                  value={email}
                  readOnly
                  className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-lg text-[#1a1a2e] bg-white focus:outline-none focus:border-[#00c853]"
                />
                <div className="absolute right-3 top-1/2 transform -translate-y-1/2 flex items-center gap-1 text-[#00c853] text-sm">
                  <Check className="w-4 h-4" />
                  <span>Подтверждён</span>
                </div>
              </div>
            </div>

            {/* Info Box */}
            <div className="bg-[#e3f2fd] border border-[#90caf9] rounded-lg p-4 mb-8">
              <p className="text-sm text-[#1565c0]">
                Ниже вы можете привязать или изменить свой аккаунт Google или Telegram для удобного и быстрого входа в кошелёк
              </p>
            </div>

            {/* Important Warning */}
            <div className="border border-gray-200 rounded-lg p-4 mb-8">
              <p className="text-sm font-semibold text-[#1a1a2e] mb-2">Важно:</p>
              <p className="text-sm text-[#64748b]">
                После привязки аккаунта ваш кошелёк будет временно заморожен на 24 часа в целях безопасности. В этот период вывод средств и некоторые операции будут недоступны.
              </p>
            </div>

            {/* Link Google Account */}
            <div className="mb-6">
              <label className="block text-sm text-[#64748b] mb-3 text-center">Привязать аккаунт Google:</label>
              <button className="w-full flex items-center justify-center gap-3 px-4 py-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors">
                <svg className="w-5 h-5" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                <span className="text-[#1a1a2e] font-medium">Google</span>
              </button>
            </div>

            {/* Link Telegram Account */}
            <div className="mb-8">
              <label className="block text-sm text-[#64748b] mb-3 text-center">Привязать аккаунт Telegram:</label>
              <button className="w-full flex items-center justify-center gap-3 px-4 py-3 bg-[#0088cc] text-white rounded-lg hover:bg-[#0077b5] transition-colors">
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z"/>
                </svg>
                <span className="font-medium">Войти через Telegram</span>
              </button>
            </div>

            {/* Additional Info */}
            <div className="text-sm text-[#64748b] space-y-4">
              <p>После привязки аккаунта вы сможете использовать его для быстрого входа без ввода пароля</p>
              
              <div>
                <p className="font-semibold text-[#1a1a2e]">Изменение (перепривязка) аккаунта</p>
                <p>Если вы хотите изменить ранее привязанный аккаунт Telegram или Google, выберите новый способ авторизации и следуйте инструкциям. Помните, что после перепривязки ваш кошелёк будет временно заморожен на 24 часа в целях безопасности.</p>
              </div>

              <p>Если вы хотите полностью удалить свой аккаунт, нажмите кнопку ниже:</p>
              
              <button className="w-full px-4 py-3 border border-red-300 text-red-500 rounded-lg hover:bg-red-50 transition-colors">
                Удалить аккаунт
              </button>
            </div>
          </div>
        </main>
      </div>
    </div>
    </div>
  );
}
